<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1 v-if="printTitle">{{ $t("soporte.titulo") }}</h1>
      <div class="toolbar d-flex justify-content-end">
        <button v-if="showCreateButton" type="button" @click.prevent="crearTicket()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-soporte">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="CodigoSecuencial" :caption="$t('soporte.grid.id')" format="00000"></DxColumn>
      <DxColumn data-field="Creador" :caption="$t('soporte.grid.creado_por')"></DxColumn>
      <DxColumn data-field="NombreEmpresaAgente" :caption="$t('soporte.grid.empresa_agente')"></DxColumn>
      <DxColumn data-field="TipoEmpresaAgente" :caption="$t('soporte.grid.tipo_empresa_agente')" cell-template="tipoEmpresaAgenteTemplate">
        <DxLookup v-bind="getLookup('empresa_agente', 'tipo')" />
      </DxColumn>
      <DxColumn data-field="Responsable" :caption="$t('soporte.grid.responsable')"></DxColumn>
      <DxColumn data-field="Tipo" :caption="$t('soporte.grid.tipo')" cell-template="tipoTemplate">
        <DxLookup v-bind="getLookup('ticket', 'tipo')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Subtipo" :caption="$t('soporte.grid.subtipo')" cell-template="subtipoTemplate">
        <DxLookup v-bind="getLookup('ticket', 'subtipo')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Asunto" :caption="$t('soporte.grid.asunto')" cell-template="asuntoTemplate" width="360px"> </DxColumn>
      <DxColumn data-field="Estado" :caption="$t('soporte.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('ticket', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Prioridad" :caption="$t('soporte.grid.prioridad')" cell-template="prioridadTemplate">
        <DxLookup v-bind="getLookup('tickets', 'prioridad')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="FechaCreacion" :caption="$t('soporte.grid.fecha_de_creacion')" data-type="datetime" format="dd/MM/yyyy HH:mm:ss"></DxColumn>
      <DxColumn data-field="FechaUltimoMensaje" :caption="$t('soporte.grid.fecha_ult_mensaje')" data-type="datetime" format="dd/MM/yyyy HH:mm:ss"></DxColumn>
      <DxColumn data-field="FechaSolicitudCierre" :caption="$t('soporte.grid.fecha_sol_cierre')" data-type="datetime" format="dd/MM/yyyy HH:mm:ss"></DxColumn>
      <DxColumn data-field="FechaCierre" :caption="$t('soporte.grid.fecha_cierre')" data-type="datetime" format="dd/MM/yyyy HH:mm:ss"></DxColumn>

      <DxColumn
        type="buttons"
        :caption="$t('soporte.grid.acciones')"
        :fixed="true"
        fixed-position="right"
        width="100px"
        :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('soporte.grid.ver_detalle'),
            icon: 'fa-solid fa-magnifying-glass text-success',
            onClick: (e: any) => verDetalle(e.row?.data.Id),
          },
        ]"
      />

      <template #tipoEmpresaAgenteTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2 `, `tipoEmpresaAgente ${data.value.replace(' ', '')}`]">
            {{ $t(`empresa_agente.tipo.${data.value}`) }}
        </div>
      </template>
      <template #estadoTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2 `, `estado ${data.value.replace(' ', '')}`]">
            {{ $t(`ticket.estado.${data.value}`) }}
        </div>
      </template>
      <template #prioridadTemplate="{ data }">
        <div
          :class="[
            `d-inline-block rounded py-1 px-2 `,
            `prioridad ${data.value != null ? data.value?.replace(' ', '') : 'sinAsignar'}`,
          ]"
        >
            {{ $t(`tickets.prioridad.${data.value}`) }} 
        </div>
      </template>
      <template #tipoTemplate="{ data }">
        <div>
          {{ $t(`ticket.tipo.${data.value}`) }}
        </div>
      </template>
      <template #subtipoTemplate="{ data }">
        <div v-if="data.value" >
          {{ $t(`ticket.subtipo.${data.value}`) }}
        </div>
      </template>
      <template #asuntoTemplate="{ data }">
        <div class="asunto-container">
          {{ data.value }}
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { useApi } from "@api/integration";
import { DxColumn, DxDataGrid, DxColumnFixing, DxLookup } from "devextreme-vue/data-grid";
import { getDefaultGridProperties } from "@helpers/grid-helper";
import type CustomStore from "devextreme/data/custom_store";
import { onMounted, ref } from "vue";
import { getLookup } from "@helpers/grid-helper";
import { useIntl } from "@services/i18n";
import { useRouter } from "@/.shared/services/routing-services";
import { useModalService } from "@services/modal-service";
import TicketForm from "@/.shared/components/soporte/TicketForm.vue";
import { CreateTicket } from "@/.shared/components/soporte/models/CreateTicket";
import { messageBoxResult } from "@/.shared/components/dialogs/message-box";
import type { DXLoadOptions } from "@/.shared/.api";

const intl = useIntl();
const api = useApi();
const router = useRouter();
const gridSource = ref<CustomStore>();
const grid = ref<DxDataGrid>();
const modal = useModalService();

const props = defineProps({
  idOperacion: {
    type: String,
    required: false,
  },
  printTitle: {
    type: Boolean,
    required: false,
    default: true,
  },
  showCreateButton: {
    type: Boolean,
    required: false,
    default: true,
  },
});

onMounted(async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      if (props.idOperacion) {
        if (q.filter != null && q.filter.length >= 0) q.filter = [["IdOperacion", "=", props.idOperacion], "and", q.filter];
        else q.filter = ["IdOperacion", "=", props.idOperacion];
      }
      return await api.callStandardApi("getTicketsForGrid", { showLoading: false }, q);
    },
  });
});

const verDetalle = async (id: string) => {
  await router.push(intl.i18Route({ name: "ticket", params: { id: id } }));
};

async function crearTicket() {
  let title = `${intl.i18n.t("ticket.creacion.titulo")}`;
  await modal.showModal(TicketForm, { model: new CreateTicket(), isCreatorSelectable: true }, { title: title, width: 800, height: 720 }).then((value) => {
    if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
  });
}
</script>

<style lang="scss" scoped>


.asunto-container {
  white-space: normal;
}





.lupa {
  color: var(--text-success-default);
}

.detalle {
  color: var(--text-primary-default);
  cursor: pointer;
}
</style>
